import { Component } from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import {
	MatterFileType,
	MatterPropertyType,
	MatterTitle,
	PropertyStatus,
	PropertyType,
	RegistrationMethodType,
} from '@models/matter';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UrlResult, UrlService } from '@services/url/url.service';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { SelectionGroup } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { PropertyUnitsModalComponent } from './components/property-units-modal/property-units-modal.component';
import { NavigationService } from '@services/nav/nav.service';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { SalePinState } from '@state/sale_pins';
import { SavedPin } from '@models/teranet';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { LtvSelectors } from '@state/matter/ltv/ltv.selectors';
import { TestHooks } from './details.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

@Component({
	selector: 'app-property-details',
	templateUrl: './details.component.html',
	styleUrls: ['./details.component.scss'],
	animations: [openClose(), fade()],
	standalone: false,
})
export class PropertyDetailsComponent {
	testHooks = TestHooks;
	public propertyTypes = PropertyType;
	public propertyStatus = PropertyStatus;

	@ViewSelectSnapshot(SelectedMatter.propertyType)
	propertyType: MatterPropertyType;
	@SelectSnapshot(SelectedMatter.isCondoUnit)
	isCondoUnit: boolean;
	@ViewSelectSnapshot(SelectedMatter.surveyDate) surveyDate: string;
	@ViewSelectSnapshot(SelectedMatter.surveyName) surveyorName: string;
	@ViewSelectSnapshot(SelectedMatter.builderNumber) builderNumber: string;
	@ViewSelectSnapshot(SelectedMatter.rollNumber) rollNumber: string;
	@ViewSelectSnapshot(SelectedMatter.nextInstallmentDate)
	nextInstallmentDate: string;
	@ViewSelectSnapshot(SelectedMatter.jurisdiction) jurisdiction: string;
	@ViewSelectSnapshot(SelectedMatter.registrationType)
	selectedRegistrationMethod: RegistrationMethodType;
	@ViewSelectSnapshot(SelectedMatter.propertyValue) propertyValue: number;

	@ViewSelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];
	@ViewSelectSnapshot(MatterTitlesState.getCommonExpensesTotal)
	commonExpensesTotal: number;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType)
	matterFileType: MatterFileType;
	@ViewSelectSnapshot(SalePinState.getParents) parentPins: SavedPin[];

	@Select(LtvSelectors.showOnPropertiesPage)
	showLtvSection$!: Observable<boolean>;

	public ICON = ICON;
	public MatterFileType = MatterFileType;
	public registrationItems = [
		{ label: 'Land titles', value: RegistrationMethodType.LAND_TITLES },
		{ label: 'Registry', value: RegistrationMethodType.REGISTRY },
	];

	public interestEstateOptions: SelectionGroup[] = [
		{
			items: [
				{ name: 'Fee Simple' },
				{ name: 'Leasehold' },
				{ name: 'Easement' },
				{ name: 'Fee simple with new easement' },
				{ name: 'Fee simple - surface rights' },
				{ name: 'Fee simple - mining rights' },
				{ name: 'Life estate remainder in fee' },
				{ name: 'Other - requires pre-approval' },
			],
		},
	];

	constructor(
		private matterDispatch: MatterDispatch,
		private navigationService: NavigationService,
		private drawerService: DrawerService,
	) {}

	selectRegistrationMethod($event) {
		this.matterDispatch.SetRegistrationMethod(this.matterId, $event);
	}

	setSurveyDate(value: string) {
		this.matterDispatch.SetSurveyDate(this.matterId, value);
	}

	setSurveyorName(value: string) {
		this.matterDispatch.SetSurveyorName(this.matterId, value);
	}

	setBuilderNumber(value: string) {
		this.matterDispatch.SetBuilderNumber(this.matterId, value);
	}

	setRollNumber(value: string) {
		this.matterDispatch.SetRollNumber(this.matterId, value);
	}

	setNextInstallmentDate(value: string) {
		this.matterDispatch.SetNextInstallmentDate(this.matterId, value);
	}

	setPropertyValue(value: number) {
		this.matterDispatch.SetPropertyValue(this.matterId, value);
	}

	addTitle() {
		this.drawerService.openDrawer(PropertyUnitsModalComponent, {
			size: 'sm',
			title: `Add ${this.isCondoUnit ? 'Unit' : 'Parcel'}`,
			noStyle: true,
		});
	}

	get adjustmentsUrl(): UrlResult {
		return UrlService.Matter_Adjustments(this.matterId);
	}

	goToTeranetSearch() {
		this.navigationService.goToMatter_TitleSearch(this.matterId);
	}
}
